import {
  Activity,
  Signature,
  Bell,
  Bookmark,
  Bot,
  Braces,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  CircleCheck,
  CirclePause,
  CircleX,
  TrendingUp,
  Copy,
  Database,
  RefreshCw,
  X,
  CircleAlert,
  Clock,
  Code,
  CodeXml,
  Combine,
  Command,
  CopyPlus,
  CornerDownLeft,
  Delete,
  Ellipsis,
  Folder,
  FolderInput,
  Funnel,
  GitFork,
  Globe,
  Import,
  Link2,
  Keyboard,
  KeyRound,
  LibraryBig,
  Lightbulb,
  List,
  ListChecks,
  ListFilter,
  LoaderCircle,
  LogOut,
  Maximize2,
  Mail,
  Merge,
  PanelLeftClose,
  Plug,
  PanelLeftOpen,
  MessageSquare,
  Minus,
  Pause,
  PencilLine,
  Play,
  Plus,
  Repeat,
  RotateCcw,
  Scan,
  Search,
  Send,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Split,
  SunMoon,
  Table,
  Trash,
  TriangleAlert,
  Upload,
  UserRound,
  Wrench,
  Workflow,
  WrapText,
  Zap,
  ChevronsDownUp,
  type LucideIcon,
  type LucideProps,
} from 'lucide-react';

import { cn } from '../lib/cn';

/** One scale: `sm` dense tables, `md` the default, `menu` menu rows, `lg` the rail. */
export const ICON_SIZE = { sm: 14, md: 16, menu: 18, lg: 20 } as const;
export type TIconSize = keyof typeof ICON_SIZE;

export type TIconProps = Omit<LucideProps, 'strokeWidth' | 'size'> & { size?: TIconSize | number };

// A number snaps onto the scale, so the dozens of hand-picked 9s and 12s cannot drift
// back below what reads; keycap glyphs are the one exception (`keyIcon`).
export const iconPx = (size: TIconSize | number = 'md'): number => {
  if (typeof size === 'string') return ICON_SIZE[size];
  if (size <= ICON_SIZE.sm) return ICON_SIZE.sm;
  return size < ICON_SIZE.lg - 1 ? ICON_SIZE.md : ICON_SIZE.lg;
};

const icon = (Glyph: LucideIcon, name: string) => {
  const Wrapped = ({ size, className, ...rest }: TIconProps) => (
    <Glyph
      size={iconPx(size)}
      strokeWidth={1.7}
      aria-hidden
      className={cn('shrink-0', className)}
      {...rest}
    />
  );
  Wrapped.displayName = name;
  return Wrapped;
};

// A key glyph is drawn at 14 inside a 20px plate; 1.7 of stroke there lands on half a
// pixel and the glyph reads smudged and off-centre. 2 at that size is 1px.
const keyIcon = (Glyph: LucideIcon, name: string) => {
  const Wrapped = ({ size = 14, className, ...rest }: TIconProps) => (
    <Glyph
      size={typeof size === 'string' ? ICON_SIZE[size] : size}
      strokeWidth={2}
      aria-hidden
      className={cn('shrink-0', className)}
      {...rest}
    />
  );
  Wrapped.displayName = name;
  return Wrapped;
};

export const EventsIcon = icon(Activity, 'EventsIcon');
export const IssuesIcon = icon(TriangleAlert, 'IssuesIcon');
export const BookmarksIcon = icon(Bookmark, 'BookmarksIcon');
export const FlowsIcon = icon(Workflow, 'FlowsIcon');
export const SourcesIcon = icon(Import, 'SourcesIcon');
export const PresetsIcon = icon(Signature, 'PresetsIcon');
export const DestinationsIcon = icon(Upload, 'DestinationsIcon');
export const TransformsIcon = icon(CodeXml, 'TransformsIcon');
export const LibraryIcon = icon(LibraryBig, 'LibraryIcon');
export const NotificationsIcon = icon(Bell, 'NotificationsIcon');
export const SettingsIcon = icon(Settings, 'SettingsIcon');
export const SearchIcon = icon(Search, 'SearchIcon');
export const ProfileIcon = icon(UserRound, 'ProfileIcon');
export const PreferencesIcon = icon(SlidersHorizontal, 'PreferencesIcon');
export const SignOutIcon = icon(LogOut, 'SignOutIcon');
export const ThemeIcon = icon(SunMoon, 'ThemeIcon');
export const KeyboardIcon = icon(Keyboard, 'KeyboardIcon');
export const ExpandIcon = icon(Maximize2, 'ExpandIcon');
export const CredentialIcon = icon(KeyRound, 'CredentialIcon');
export const SavedViewIcon = icon(ListFilter, 'SavedViewIcon');
export const RequestIcon = icon(Clock, 'RequestIcon');
export const AlertIcon = icon(CircleAlert, 'AlertIcon');
export const PlayIcon = icon(Play, 'PlayIcon');
export const PlusIcon = icon(Plus, 'PlusIcon');
export const CheckIcon = icon(Check, 'CheckIcon');
export const ChevronDownIcon = icon(ChevronDown, 'ChevronDownIcon');
export const SwitcherIcon = icon(ChevronsUpDown, 'SwitcherIcon');
export const ChevronUpIcon = icon(ChevronUp, 'ChevronUpIcon');
export const ChevronRightIcon = icon(ChevronRight, 'ChevronRightIcon');
export const DoneIcon = icon(CircleCheck, 'DoneIcon');
export const FailedIcon = icon(CircleX, 'FailedIcon');
export const HeldIcon = icon(CirclePause, 'HeldIcon');
export const GrowthIcon = icon(TrendingUp, 'GrowthIcon');
export const ClockIcon = icon(Clock, 'ClockIcon');
export const CopyIcon = icon(Copy, 'CopyIcon');
export const LinkIcon = icon(Link2, 'LinkIcon');
export const WrapIcon = icon(WrapText, 'WrapIcon');
export const CollapseAllIcon = icon(ChevronsDownUp, 'CollapseAllIcon');
export const ExpandAllIcon = icon(ChevronsUpDown, 'ExpandAllIcon');
export const RedeliverIcon = icon(RefreshCw, 'RedeliverIcon');
export const CloseIcon = icon(X, 'CloseIcon');
export const FilterIcon = icon(Funnel, 'FilterIcon');
export const RouteIcon = icon(GitFork, 'RouteIcon');
export const SplitIcon = icon(Split, 'SplitIcon');
export const AggregateIcon = icon(Combine, 'AggregateIcon');
export const MergeIcon = icon(Merge, 'MergeIcon');
export const LoopIcon = icon(Repeat, 'LoopIcon');
export const WaitIcon = icon(Clock, 'WaitIcon');
export const CodeIcon = icon(Code, 'CodeIcon');
export const SetFieldsIcon = icon(PencilLine, 'SetFieldsIcon');
export const LookupIcon = icon(Table, 'LookupIcon');
export const AiIcon = icon(Sparkles, 'AiIcon');
export const HttpIcon = icon(Globe, 'HttpIcon');
export const MessageIcon = icon(MessageSquare, 'MessageIcon');
export const SendIcon = icon(Send, 'SendIcon');
export const MailIcon = icon(Mail, 'MailIcon');
export const ActionIcon = icon(Zap, 'ActionIcon');
export const AgentIcon = icon(Bot, 'AgentIcon');
export const HookpipeActionIcon = icon(Wrench, 'HookpipeActionIcon');
export const McpIcon = icon(Plug, 'McpIcon');
export const KnowledgeIcon = icon(Database, 'KnowledgeIcon');
export const PersonIcon = icon(UserRound, 'PersonIcon');
export const BackIcon = icon(ChevronLeft, 'BackIcon');
export const ListIcon = icon(List, 'ListIcon');
export const InsertValueIcon = icon(Braces, 'InsertValueIcon');
export const SkipIcon = icon(Pause, 'SkipIcon');
export const DuplicateIcon = icon(CopyPlus, 'DuplicateIcon');
export const DeleteIcon = icon(Trash, 'DeleteIcon');
export const ZoomOutIcon = icon(Minus, 'ZoomOutIcon');
export const FitViewIcon = icon(Scan, 'FitViewIcon');
export const MoreIcon = icon(Ellipsis, 'MoreIcon');
export const FolderIcon = icon(Folder, 'FolderIcon');
export const MoveToFolderIcon = icon(FolderInput, 'MoveToFolderIcon');
export const StopIcon = icon(Minus, 'StopIcon');
export const PauseIcon = icon(Pause, 'PauseIcon');
export const RecoveredIcon = icon(RotateCcw, 'RecoveredIcon');
export const ThinkingIcon = icon(Lightbulb, 'ThinkingIcon');
export const AnswerIcon = icon(ListChecks, 'AnswerIcon');
export const SidebarCollapseIcon = icon(PanelLeftClose, 'SidebarCollapseIcon');
export const SidebarExpandIcon = icon(PanelLeftOpen, 'SidebarExpandIcon');
export const CommandIcon = keyIcon(Command, 'CommandIcon');
export const EnterIcon = keyIcon(CornerDownLeft, 'EnterIcon');
export const BackspaceIcon = keyIcon(Delete, 'BackspaceIcon');

export const SpinnerIcon = ({ size = 'sm', className, ...rest }: TIconProps) => (
  <LoaderCircle
    size={iconPx(size)}
    strokeWidth={3}
    aria-hidden
    className={cn('shrink-0 animate-[hp-spin_0.9s_linear_infinite]', className)}
    {...rest}
  />
);
